import React from 'react';
import { Wallet, ExternalLink, AlertTriangle } from 'lucide-react';

/**
 * ShopAnywhereCallout — yellow highlight shown on driver-side order cards
 * when the order's fulfilment_mode is "shop_anywhere". Tells the driver that
 * they need to physically purchase the items at the external merchant on the
 * customer's behalf and lists everything they need to buy.
 */
const ShopAnywhereCallout = ({ order, compact = false }) => {
  if (order?.fulfilment_mode !== 'shop_anywhere') return null;
  const url = order.external_vendor_url;
  const merchant = order.vendor_name || 'external merchant';
  const subtotal = Number(order.subtotal || 0);

  if (compact) {
    return (
      <span data-testid="shop-anywhere-badge"
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-semibold uppercase tracking-wider">
        <Wallet className="h-3 w-3" /> Pickup + pay
      </span>
    );
  }

  return (
    <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="shop-anywhere-callout">
      <div className="flex items-center gap-2 text-amber-800">
        <AlertTriangle className="h-4 w-4" />
        <span className="text-[11px] font-semibold uppercase tracking-wider">Pickup + pay at merchant</span>
      </div>
      <p className="text-sm text-amber-900 mt-1.5">
        Buy these items at <b>{merchant}</b> for the customer. ShipitNow will reimburse you for ${subtotal.toFixed(2)} (subtotal) plus your delivery fee.
      </p>
      {url && (
        <a href={url} target="_blank" rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-sm text-amber-900 hover:text-amber-700 underline" data-testid="shop-anywhere-merchant-link">
          <ExternalLink className="h-3.5 w-3.5" /> Open merchant page
        </a>
      )}
      {Array.isArray(order.items) && order.items.length > 0 && (
        <details className="mt-2 group" data-testid="shop-anywhere-items">
          <summary className="text-sm font-medium text-amber-900 cursor-pointer select-none">Items to buy ({order.items.length})</summary>
          <ul className="mt-2 space-y-1 text-sm text-amber-900">
            {order.items.map((it, i) => (
              <li key={it.id || i} className="flex items-center justify-between">
                <span><b>{it.qty}×</b> {it.name}</span>
                <span className="tabular-nums">${(Number(it.price) || 0).toFixed(2)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default ShopAnywhereCallout;
